<template>
  <b-modal
    id="modal-season-pass-help"
    :hide-header="true"
    :hide-footer="true"
    modal-class="modal-season-pass-help"
    @shown="onOpen"
  >
    <template slot="default">
      <a href="#" class="close-button" @click="closeModal"></a>
      <div class="bg" :style="backgroundStyle"></div>

      <div class="content">
        <div v-if="hasBackendContent" class="body" v-html="contentHtml"></div>

        <template v-else>
          <div v-if="currentPage === 1" class="body page-intro">
            {{ introText }}
          </div>

          <div v-else-if="currentPage === 2" class="body page-intro">
            {{ manualText }}
          </div>

          <div v-else-if="currentPage === 3" class="body page-cta">
            <div v-if="premiumLabel" class="page-intro page-cta-text">
              {{ premiumText }}
            </div>

            <div
              v-if="isPremium"
              class="premium-button d-inline-flex align-items-center justify-content-around"
            >
              <div class="text1">
                {{ $t("seasonpass.tier.activated", { 0: premiumLabel }) }}
              </div>
              <div class="check-icon"></div>
            </div>
            <div
              v-else-if="ctaText"
              class="premium-button active pointer d-inline-flex align-items-center justify-content-between"
              @click="onBuyPremium"
            >
              <div class="text1">{{ ctaText }}</div>
              <div v-if="premiumPrices.length" class="premium-prices">
                <div
                  v-for="price in premiumPrices"
                  :key="price.currency"
                  class="premium-price"
                >
                  <span>{{ formatNumber(price.amount, true) }}</span>
                  <img :src="price.icon" :alt="price.currency" />
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div v-if="!hasBackendContent" class="bottom-menu to-center-abs">
        <div v-if="currentPage > 1" class="arrow left" @click="prevPage"></div>
        <div
          v-if="currentPage < maxPages"
          class="arrow right"
          @click="nextPage"
        ></div>
      </div>
    </template>
  </b-modal>
</template>

<script>
export default {
  name: "ModalSeasonPassHelp",
  props: {
    helpConfig: {
      type: Object,
      default: () => ({}),
    },
    passId: {
      type: [String, Number],
      default: null,
    },
    passName: {
      type: String,
      default: "",
    },
    levelsCount: {
      type: Number,
      default: 0,
    },
    premiumLabel: {
      type: String,
      default: "",
    },
    premiumCta: {
      type: String,
      default: "",
    },
    isPremium: {
      type: Boolean,
      default: false,
    },
    premiumPrices: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      currentPage: 1,
      maxPages: 3,
    };
  },
  computed: {
    locale() {
      return this.$store.state.locale || "de";
    },
    backgroundStyle() {
      const url = this.getConfigUrl("background");
      return url ? { backgroundImage: `url("${url}")` } : {};
    },
    headline() {
      const h = this.helpConfig && this.helpConfig.headline;
      if (!h) return this.passName;
      return typeof h === "string" ? h : h[this.locale] || h.fallback || "";
    },
    hasBackendContent() {
      return !!(this.helpConfig && this.helpConfig.content);
    },
    contentHtml() {
      const c = this.helpConfig && this.helpConfig.content;
      if (!c) return "";
      if (typeof c === "string") return c;
      return c[this.locale] || c.fallback || "";
    },
    introText() {
      return this.$t("seasonpass.help.intro", { 0: this.passName });
    },
    manualText() {
      return this.$t("seasonpass.help.manual", {
        0: this.passName,
        1: this.levelsCount,
      });
    },
    premiumText() {
      return this.$t("seasonpass.help.premium", { 0: this.premiumLabel });
    },
    ctaText() {
      if (this.premiumCta) return this.premiumCta;
      if (!this.premiumLabel) return "";
      return this.$t("seasonpass.tier.activate.action", {
        0: this.premiumLabel,
      });
    },
  },
  methods: {
    onOpen() {
      this.currentPage = 1;
    },
    prevPage() {
      if (this.currentPage <= 1) return;
      this.currentPage--;
    },
    nextPage() {
      if (this.currentPage >= this.maxPages) return;
      this.currentPage++;
    },
    closeModal() {
      this.$bvModal.hide("modal-season-pass-help");
    },
    onBuyPremium() {
      this.$emit("buy-premium");
      this.$bvModal.hide("modal-season-pass-help");
    },
    getConfigUrl(key) {
      const cfg = this.helpConfig || {};
      return (
        cfg[key] ||
        cfg[`${key}Url`] ||
        cfg[`${key}_url`] ||
        cfg[`${key}Path`] ||
        cfg[`${key}_path`] ||
        cfg[`${key}PathFull`] ||
        cfg[`${key}_path_full`] ||
        ""
      );
    },
  },
};
</script>

<style lang="scss">
.modal-season-pass-help {
  .bg {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    background-position: center;
    background-size: 100% 100%;
    background-repeat: no-repeat;
    pointer-events: none;
  }

  .content {
    position: relative;
    z-index: 1;
    color: white;
    padding: 60px 80px;
    min-height: 535px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .headline {
      font-family: Ubuntu-Bold, sans-serif;
      font-size: 30px;
      text-transform: uppercase;
      margin-top: 40px;
      margin-bottom: 24px;
      text-align: center;
    }

    .body {
      font-size: 18px;
      line-height: 1.4;
    }

    .bullet-list {
      list-style: disc inside;
      padding: 0;
      margin: 0 auto;
      max-width: 540px;
      text-align: center;

      li {
        margin-bottom: 12px;
      }
    }

    .page-intro {
      font-size: 24px;
      line-height: 1.5;
      text-align: center;
      max-width: 600px;
      margin: 0 auto;
      padding: 0 20px;
    }

    .page-cta {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      min-height: 180px;
      gap: 24px;
    }

    .page-cta .page-cta-text {
      margin: 0 auto;
      max-width: 600px;
    }

    .page-cta .premium-button {
      position: relative;
      transform: none;
      left: auto;
      bottom: auto;
    }

    .premium-button {
      position: absolute;
      left: 50%;
      bottom: -15px;
      transform: translateX(-50%);
      width: 400px;
      height: 50px;
      padding-left: 10px;
      padding-right: 10px;
      font-family: kanit-semibold, sans-serif;
      text-transform: uppercase;
      color: white;
      background: linear-gradient(
          to bottom,
          #ffffff,
          #48c919 20%,
          #2dac00 47%,
          $text-shadow-dark-green
        ),
        linear-gradient(
          to bottom,
          $bg-gradient-golden,
          #f7ffff 41%,
          #ffee51 75%,
          $bg-gradient-golden
        );
      border: double 2px transparent;
      background-origin: border-box;
      background-clip: padding-box, border-box;
      border-radius: 25.2px;

      &.active {
        cursor: pointer;

        &:hover {
          background: linear-gradient(
              to bottom,
              $text-shadow-dark-green,
              #2dac00 53%,
              #48c919 80%,
              #ffffff
            ),
            linear-gradient(
              to bottom,
              $bg-gradient-golden,
              #f7ffff 41%,
              #ffee51 75%,
              $bg-gradient-golden
            );

          border: double 2px transparent;
          background-origin: border-box;
          background-clip: padding-box, border-box;
          border-radius: 25.2px;
        }
      }

      .text1 {
        font-size: 16px;
        text-shadow: 0 0 4px $drop-shadow-dark;
        line-height: 1;
        flex: 1;
        text-align: center;
      }

      .premium-prices {
        display: inline-flex;
        align-items: center;
        justify-content: flex-end;
        gap: 12px;
        flex-shrink: 0;
        margin-left: 8px;
      }

      .premium-price {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 20px;
        line-height: 1;
        text-shadow: 0 0 4px $drop-shadow-dark;
        white-space: nowrap;

        img {
          width: 28px;
          height: 28px;
          object-fit: contain;
          filter: drop-shadow(0 0 3px $drop-shadow-dark);
        }
      }

      .check-icon {
        position: absolute;
        right: 10px;
        top: 0;
        width: 48px;
        height: 48px;
        background: url(~@/assets/img/events/winter-bonus-jagd/green-checkmark-circle.png)
          right/100% 100% no-repeat;
      }
    }
  }
}
</style>

<style lang="scss">
$modal-width: 822px;
$modal-height: 535px;

.modal-season-pass-help {
  .modal-dialog {
    min-width: $modal-width;
    max-width: $modal-width;
    width: 100%;
    height: $modal-height;
    margin-top: 80px;
  }

  .modal-content {
    width: 100%;
    height: 100%;
    background: none;
    border: none;
  }

  .modal-body {
    width: 100%;
    height: 100%;
    overflow: visible;
    padding: 0;
    position: relative;
  }

  .close-button {
    position: absolute;
    right: -15px;
    top: 20px;
    width: 36px;
    height: 36px;
    z-index: 999;
  }

  .bottom-menu {
    top: 50%;
    width: 77%;
    z-index: 50;
    pointer-events: none;

    .arrow {
      position: absolute;
      top: -25px;
      width: 48px;
      height: 48px;
      cursor: pointer;
      pointer-events: auto;
      z-index: 51;

      &:hover {
        filter: $hover-shadow;
      }

      &.left {
        background: url(~@/assets/img/events/winter-bonus-jagd/btn-slide-arrow.png)
          bottom center/100% 100% no-repeat;
        left: -120px;
        transform: scaleX(-1);
      }

      &.right {
        background: url(~@/assets/img/events/winter-bonus-jagd/btn-slide-arrow.png)
          bottom center/100% 100% no-repeat;
        right: -120px;
      }
    }
  }
}
</style>
